import { Loader2 } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useGeneratePreview, usePdfCompletedSessions } from "@/lib/hooks/use-pdf-templates";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import { getRelativeTime } from "@/lib/utils";
import { T } from "./preview-button.i18n";

type PreviewButtonProps = {
  templateId: string;
  flowId: string;
};

export function PreviewButton({ templateId, flowId: _flowId }: PreviewButtonProps) {
  const { sessions, isLoading } = usePdfCompletedSessions(templateId);
  const previewMutation = useGeneratePreview();
  const [error, setError] = useState<string | null>(null);
  const { userLanguage } = useUserLanguage();
  const t = useT(T);

  const handlePreview = async (flowInstanceId: string) => {
    setError(null);

    try {
      const result = await previewMutation.mutateAsync({ templateId, flowInstanceId });

      if (!result.success || !result.data) {
        setError(result.error || t.failedToCreate);
        return;
      }

      // Decode base64 to blob
      const byteCharacters = atob(result.data.base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });

      // Trigger download
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = result.data.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : t.failedToCreate);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" loading={previewMutation.isPending}>
          {t.preview}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[36rem]">
        <DropdownMenuLabel>{t.selectSession}</DropdownMenuLabel>
        <DropdownMenuSeparator />

        {isLoading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        ) : sessions.length === 0 ? (
          <div className="py-4 px-2 text-center text-sm text-muted-foreground">
            {t.noCompletedSessions}
          </div>
        ) : (
          sessions.map((session) => (
            <DropdownMenuItem
              key={session.id}
              onClick={() => handlePreview(session.id)}
              disabled={previewMutation.isPending}
            >
              <span className="truncate">
                {session.clientName}
                {session.name && (
                  <span className="text-muted-foreground ml-1">({session.name})</span>
                )}
              </span>
              <span className="ml-auto text-xs text-muted-foreground">
                {getRelativeTime(session.completedAt, userLanguage)}
              </span>
            </DropdownMenuItem>
          ))
        )}

        {error && (
          <>
            <DropdownMenuSeparator />
            <div className="py-2 px-2 text-sm text-destructive">{error}</div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
